import { Controller } from "@hotwired/stimulus"
import { ChartAdapters } from "../utils/chart_adapters"

export default class extends Controller {
  static targets = [
    "chartType",
    "dimensions", 
    "measures",
    "canvas",
    "chartContainer",
    "chartPreview",
    "colorScheme",
    "chartTitle",
    "showLegend",
    "legendPosition",
    "axisLabels",
    "xAxisLabel",
    "yAxisLabel",
    "chartSize",
    "chartWidth",
    "chartHeight"
  ]

  static values = {
    reportData: Array,
    availableFields: Array,
    chartConfiguration: Object,
    chartType: String
  }

  connect() {
    this.initializeConfiguration()
    this.setupChartTypes()
    this.currentChart = null
  }

  disconnect() {
    if (this.currentChart) {
      this.currentChart.destroy()
      this.currentChart = null
    }
  }

  initializeConfiguration() {
    this.chartConfigurationValue = {
      type: 'bar',
      dimensions: [],
      measures: [],
      title: '',
      showLegend: true,
      legendPosition: 'top',
      colorScheme: 'default',
      xAxisLabel: '',
      yAxisLabel: '',
      width: 600,
      height: 400,
      responsive: true,
      ...this.chartConfigurationValue
    }
  }

  setupChartTypes() {
    if (!this.hasChartTypeTarget) return

    const chartTypes = [
      { value: 'bar', label: 'Bar Chart', icon: 'fas fa-chart-bar' },
      { value: 'line', label: 'Line Chart', icon: 'fas fa-chart-line' },
      { value: 'pie', label: 'Pie Chart', icon: 'fas fa-chart-pie' },
      { value: 'doughnut', label: 'Doughnut Chart', icon: 'fas fa-chart-pie' },
      { value: 'area', label: 'Area Chart', icon: 'fas fa-chart-area' }
    ]

    const options = chartTypes.map(type => `
      <option value="${type.value}" ${type.value === this.chartConfigurationValue.type ? 'selected' : ''}>
        ${type.label}
      </option>
    `).join('')

    this.chartTypeTarget.innerHTML = options
    this.updateChartTypeValue(this.chartConfigurationValue.type)
  }

  // Chart Type Selection
  selectChartType(event) {
    const chartType = event.target.value
    this.updateChartTypeValue(chartType)
    this.chartConfigurationValue.type = chartType
    this.validateChartConfiguration()
    this.updateChart()
    this.dispatchConfigurationChange()
  }

  updateChartTypeValue(chartType) {
    this.chartTypeValue = chartType
    this.updateDimensionMeasureRequirements(chartType)
  }

  updateDimensionMeasureRequirements(chartType) {
    // Update UI to show requirements for different chart types
    const requirementsText = this.getChartTypeRequirements(chartType)
    const requirementsElement = this.element.querySelector('.chart-requirements')
    if (requirementsElement) {
      requirementsElement.innerHTML = requirementsText
    }
  }

  getChartTypeRequirements(chartType) {
    switch (chartType) {
      case 'pie':
      case 'doughnut':
        return '<small class="text-muted"><i class="fas fa-info-circle"></i> Requires 1 dimension and 1 measure</small>'
      case 'bar':
      case 'line':
      case 'area':
        return '<small class="text-muted"><i class="fas fa-info-circle"></i> Requires 1+ dimensions and 1+ measures</small>'
      default:
        return '<small class="text-muted"><i class="fas fa-info-circle"></i> Select dimensions and measures for your chart</small>'
    }
  }

  // Dimension and Measure Selection
  renderFieldSelectors() {
    if (!this.availableFieldsValue || this.availableFieldsValue.length === 0) return

    this.renderDimensionSelector()
    this.renderMeasureSelector()
  }

  renderDimensionSelector() {
    if (!this.hasDimensionsTarget) return

    const dimensionFields = this.availableFieldsValue.filter(field => 
      ['string', 'date', 'datetime', 'boolean'].includes(field.type)
    )

    const options = dimensionFields.map(field => `
      <option value="${field.name}" ${this.chartConfigurationValue.dimensions.includes(field.name) ? 'selected' : ''}>
        ${field.display_name}
      </option>
    `).join('')

    this.dimensionsTarget.innerHTML = `
      <option value="">Select dimension...</option>
      ${options}
    `
  }

  renderMeasureSelector() {
    if (!this.hasMeasuresTarget) return

    const measureFields = this.availableFieldsValue.filter(field => 
      ['number', 'currency', 'integer'].includes(field.type)
    )

    const options = measureFields.map(field => `
      <option value="${field.name}" ${this.chartConfigurationValue.measures.includes(field.name) ? 'selected' : ''}>
        ${field.display_name}
      </option>
    `).join('')

    this.measuresTarget.innerHTML = `
      <option value="">Select measure...</option>
      ${options}
    `
  }

  selectDimension(event) {
    const dimensionName = event.target.value
    
    if (dimensionName && !this.chartConfigurationValue.dimensions.includes(dimensionName)) {
      // For pie/doughnut charts, only allow one dimension
      if (['pie', 'doughnut'].includes(this.chartConfigurationValue.type)) {
        this.chartConfigurationValue.dimensions = [dimensionName]
      } else {
        this.chartConfigurationValue.dimensions.push(dimensionName)
      }
      this.updateFieldSelectors()
      this.validateChartConfiguration()
      this.updateChart()
      this.dispatchConfigurationChange()
    }
  }

  selectMeasure(event) {
    const measureName = event.target.value
    
    if (measureName && !this.chartConfigurationValue.measures.includes(measureName)) {
      // For pie/doughnut charts, only allow one measure
      if (['pie', 'doughnut'].includes(this.chartConfigurationValue.type)) {
        this.chartConfigurationValue.measures = [measureName]
      } else {
        this.chartConfigurationValue.measures.push(measureName)
      }
      this.updateFieldSelectors()
      this.validateChartConfiguration()
      this.updateChart()
      this.dispatchConfigurationChange()
    }
  }

  removeDimension(event) {
    const dimensionName = event.target.dataset.dimensionName
    this.chartConfigurationValue.dimensions = this.chartConfigurationValue.dimensions.filter(
      dim => dim !== dimensionName
    )
    this.updateFieldSelectors()
    this.validateChartConfiguration()
    this.updateChart()
    this.dispatchConfigurationChange()
  }

  removeMeasure(event) {
    const measureName = event.target.dataset.measureName
    this.chartConfigurationValue.measures = this.chartConfigurationValue.measures.filter(
      measure => measure !== measureName
    )
    this.updateFieldSelectors()
    this.validateChartConfiguration()
    this.updateChart()
    this.dispatchConfigurationChange()
  }

  updateFieldSelectors() {
    this.renderSelectedDimensions()
    this.renderSelectedMeasures()
    this.renderDimensionSelector()
    this.renderMeasureSelector()
  }

  renderSelectedDimensions() {
    const container = this.element.querySelector('.selected-dimensions')
    if (!container) return

    const selectedDimensions = this.chartConfigurationValue.dimensions.map(dimensionName => {
      const field = this.availableFieldsValue.find(f => f.name === dimensionName)
      return `
        <span class="badge bg-primary me-2 mb-2">
          ${field ? field.display_name : dimensionName}
          <button type="button" class="btn-close btn-close-white ms-1" 
                  data-dimension-name="${dimensionName}"
                  data-action="click->chart-builder#removeDimension"></button>
        </span>
      `
    }).join('')

    container.innerHTML = selectedDimensions || '<span class="text-muted">No dimensions selected</span>'
  }

  renderSelectedMeasures() {
    const container = this.element.querySelector('.selected-measures')
    if (!container) return

    const selectedMeasures = this.chartConfigurationValue.measures.map(measureName => {
      const field = this.availableFieldsValue.find(f => f.name === measureName)
      return `
        <span class="badge bg-success me-2 mb-2">
          ${field ? field.display_name : measureName}
          <button type="button" class="btn-close btn-close-white ms-1" 
                  data-measure-name="${measureName}"
                  data-action="click->chart-builder#removeMeasure"></button>
        </span>
      `
    }).join('')

    container.innerHTML = selectedMeasures || '<span class="text-muted">No measures selected</span>'
  }

  // Chart Configuration Validation
  validateChartConfiguration() {
    const { type, dimensions, measures } = this.chartConfigurationValue
    let isValid = false
    let validationMessage = ''

    try {
      isValid = ChartAdapters.validateChartConfiguration(type, dimensions, measures)
      if (!isValid) {
        const requirements = ChartAdapters.getChartRequirements(type)
        validationMessage = `Requires ${requirements.requiredDimensions} dimension(s) and ${requirements.requiredMeasures} measure(s)`
      }
    } catch (e) {
      validationMessage = 'Please select a chart type'
    }

    this.updateValidationDisplay(isValid, validationMessage)
    return isValid
  }

  updateValidationDisplay(isValid, message) {
    const validationElement = this.element.querySelector('.chart-validation')
    if (!validationElement) return

    if (isValid) {
      validationElement.innerHTML = '<small class="text-success"><i class="fas fa-check-circle"></i> Chart configuration is valid</small>'
      validationElement.className = 'chart-validation text-success'
    } else if (message) {
      validationElement.innerHTML = `<small class="text-warning"><i class="fas fa-exclamation-triangle"></i> ${message}</small>`
      validationElement.className = 'chart-validation text-warning'
    } else {
      validationElement.innerHTML = ''
      validationElement.className = 'chart-validation'
    }
  }

  // Chart Generation and Preview
  async updateChart() {
    if (!this.validateChartConfiguration() || !this.reportDataValue || this.reportDataValue.length === 0) {
      this.clearChart()
      return
    }

    try {
      const chartData = this.transformDataForChart()
      const chartConfig = this.buildChartConfig(chartData)
      await this.renderChart(chartConfig)
    } catch (error) {
      console.error('Error updating chart:', error)
      this.showChartError('Failed to generate chart preview')
    }
  }

  transformDataForChart() {
    const { type, dimensions, measures, colorScheme } = this.chartConfigurationValue
    
    if (!this.reportDataValue || this.reportDataValue.length === 0) {
      throw new Error('No data available for chart')
    }

    // Build field mappings from available fields for display names
    const fieldMappings = {}
    if (this.availableFieldsValue) {
      this.availableFieldsValue.forEach(f => {
        fieldMappings[f.name] = f.display_name || f.name
      })
    }

    return ChartAdapters.transformDataForChart(type, this.reportDataValue, dimensions, measures, {
      colorScheme: colorScheme || 'default',
      fieldMappings
    })
  }

  generateColors(count, startIndex = 0) {
    const colorScheme = this.chartConfigurationValue.colorScheme || 'default'
    return ChartAdapters.generateColorPalette(count, colorScheme, startIndex)
  }

  buildChartConfig(chartData) {
    const { type, title, showLegend, legendPosition, xAxisLabel, yAxisLabel } = this.chartConfigurationValue

    const defaultOptions = ChartAdapters.getDefaultOptions(type, {
      title,
      showLegend,
      legendPosition,
      xAxisLabel,
      yAxisLabel
    })

    return {
      type: type === 'area' ? 'line' : type,
      data: chartData,
      options: defaultOptions
    }
  }

  async renderChart(config) {
    if (!this.hasCanvasTarget) return

    // Destroy existing chart and clear the reference BEFORE awaiting the dynamic
    // import — otherwise a concurrent renderChart() call would see the destroyed
    // instance, call .destroy() again, and both calls would write competing Chart
    // instances to this.currentChart.
    if (this.currentChart) {
      this.currentChart.destroy()
      this.currentChart = null
    }

    // Create new chart
    const { default: Chart } = await import('chart.js/auto')
    const ctx = this.canvasTarget.getContext('2d')
    this.currentChart = new Chart(ctx, config)

    // Add drill-down capability via ChartAdapters
    ChartAdapters.addDrillDownCapability(this.currentChart, this.onChartDrillDown.bind(this))

    // Show chart container
    if (this.hasChartContainerTarget) {
      this.chartContainerTarget.style.display = 'block'
    }
  }

  onChartDrillDown(detail) {
    const event = new CustomEvent('chart-drill-down', {
      detail: {
        label: detail.label,
        value: detail.value,
        datasetIndex: detail.datasetIndex,
        index: detail.index
      },
      bubbles: true
    })
    this.element.dispatchEvent(event)
  }

  clearChart() {
    if (this.currentChart) {
      this.currentChart.destroy()
      this.currentChart = null
    }

    if (this.hasChartContainerTarget) {
      this.chartContainerTarget.style.display = 'none'
    }
  }

  showChartError(message) {
    if (this.hasChartPreviewTarget) {
      this.chartPreviewTarget.innerHTML = `
        <div class="alert alert-warning">
          <i class="fas fa-exclamation-triangle"></i> ${message}
        </div>
      `
    }
  }

  // Chart Customization
  updateChartTitle(event) {
    this.chartConfigurationValue.title = event.target.value
    this.updateChart()
    this.dispatchConfigurationChange()
  }

  toggleLegend(event) {
    this.chartConfigurationValue.showLegend = event.target.checked
    this.updateChart()
    this.dispatchConfigurationChange()
  }

  updateLegendPosition(event) {
    this.chartConfigurationValue.legendPosition = event.target.value
    this.updateChart()
    this.dispatchConfigurationChange()
  }

  updateColorScheme(event) {
    this.chartConfigurationValue.colorScheme = event.target.value
    this.updateChart()
    this.dispatchConfigurationChange()
  }

  updateXAxisLabel(event) {
    this.chartConfigurationValue.xAxisLabel = event.target.value
    this.updateChart()
    this.dispatchConfigurationChange()
  }

  updateYAxisLabel(event) {
    this.chartConfigurationValue.yAxisLabel = event.target.value
    this.updateChart()
    this.dispatchConfigurationChange()
  }

  updateChartSize(event) {
    const dimension = event.target.dataset.dimension
    const value = parseInt(event.target.value)
    
    if (dimension === 'width') {
      this.chartConfigurationValue.width = value
    } else if (dimension === 'height') {
      this.chartConfigurationValue.height = value
    }

    this.updateCanvasSize()
    this.updateChart()
    this.dispatchConfigurationChange()
  }

  updateCanvasSize() {
    if (this.hasCanvasTarget) {
      this.canvasTarget.style.width = `${this.chartConfigurationValue.width}px`
      this.canvasTarget.style.height = `${this.chartConfigurationValue.height}px`
    }
  }

  // Data Integration
  updateReportData(event) {
    const { reportData, availableFields } = event.detail
    this.reportDataValue = reportData
    this.availableFieldsValue = availableFields
    this.renderFieldSelectors()
    this.updateChart()
  }

  // Configuration Management
  loadChartConfiguration(configuration) {
    this.chartConfigurationValue = { ...this.chartConfigurationValue, ...configuration }
    this.updateUI()
    this.updateChart()
  }

  updateUI() {
    // Update form controls to match configuration
    if (this.hasChartTypeTarget) {
      this.chartTypeTarget.value = this.chartConfigurationValue.type
    }
    if (this.hasChartTitleTarget) {
      this.chartTitleTarget.value = this.chartConfigurationValue.title
    }
    if (this.hasShowLegendTarget) {
      this.showLegendTarget.checked = this.chartConfigurationValue.showLegend
    }
    if (this.hasLegendPositionTarget) {
      this.legendPositionTarget.value = this.chartConfigurationValue.legendPosition
    }
    if (this.hasColorSchemeTarget) {
      this.colorSchemeTarget.value = this.chartConfigurationValue.colorScheme
    }
    if (this.hasXAxisLabelTarget) {
      this.xAxisLabelTarget.value = this.chartConfigurationValue.xAxisLabel
    }
    if (this.hasYAxisLabelTarget) {
      this.yAxisLabelTarget.value = this.chartConfigurationValue.yAxisLabel
    }
    if (this.hasChartWidthTarget) {
      this.chartWidthTarget.value = this.chartConfigurationValue.width
    }
    if (this.hasChartHeightTarget) {
      this.chartHeightTarget.value = this.chartConfigurationValue.height
    }

    this.updateFieldSelectors()
    this.updateCanvasSize()
  }

  getChartConfiguration() {
    return this.chartConfigurationValue
  }

  // Event Dispatching
  dispatchConfigurationChange() {
    const event = new CustomEvent('chart-configuration-changed', {
      detail: {
        configuration: this.chartConfigurationValue,
        isValid: this.validateChartConfiguration()
      },
      bubbles: true
    })
    this.element.dispatchEvent(event)
  }

  // Export Functionality
  exportChartAsImage() {
    if (!this.currentChart) {
      console.warn('No chart available for export')
      return null
    }

    return ChartAdapters.exportChartAsImage(this.currentChart)
  }

  getChartImageData() {
    if (!this.currentChart) {
      return null
    }

    return ChartAdapters.getChartImageData(this.currentChart)
  }
}